/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import React, { createContext, useContext, useMemo } from "react";

import { DefaultButton } from "../components/DefaultButton";
import { DefaultLoaderSpinner } from "../components/DefaultLoaderSpinner";
import { DefaultSlider } from "../components/DefaultSlider";
import { PlayerComponents } from "../types";

const defaults: Required<
  Pick<PlayerComponents, "Button" | "Slider" | "LoaderSpinner">
> = {
  Button: DefaultButton,
  Slider: DefaultSlider,
  LoaderSpinner: DefaultLoaderSpinner,
};

const PlayerComponentsContext = createContext<PlayerComponents>(defaults);

export const PlayerComponentsProvider: React.FC<{
  components?: PlayerComponents;
  children: React.ReactNode;
}> = ({ components, children }) => {
  const merged = useMemo(
    () => ({
      ...defaults,
      ...components,
    }),
    [components],
  );

  return (
    <PlayerComponentsContext.Provider value={merged}>
      {children}
    </PlayerComponentsContext.Provider>
  );
};

export const usePlayerComponents = (): Required<
  Pick<PlayerComponents, "Button" | "Slider" | "LoaderSpinner">
> &
  PlayerComponents => {
  const ctx = useContext(PlayerComponentsContext);
  return {
    ...defaults,
    ...ctx,
  };
};
